import { Button } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { StyleSheet, Text, View } from 'react-native';

import { CardModal } from 'components/card-modal';
import LoadingAnimation from 'components/loading-animation';
import Picture from 'components/picture';
import { useLoyaltyHowItWorks } from 'hooks/use-loyalty-how-it-works';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { primitive } from 'styles/constants/primitives';
import { blockContentToPlainText } from 'utils/sanity';

const FEATURE_LOYALTY_HOW_IT_WORKS_ID = 'feature-loyalty-how-it-works-ui-singleton';

export default function HowItWorks() {
  const router = useRouter();
  const { loading, pageTitle, callToActionText, steps } = useLoyaltyHowItWorks(
    FEATURE_LOYALTY_HOW_IT_WORKS_ID
  );

  const handleOnDismiss = () => router.dismiss();
  const isDesktopWeb = useIsDesktopWebBreakpoint();

  if (loading) {
    return (
      <View style={styles.loadingContainer} accessibilityRole="progressbar">
        <LoadingAnimation />
      </View>
    );
  }

  return (
    <CardModal
      dismissType="close"
      customHeaderBackgroundColor={primitive.$white}
      scrollContainerStyle={{
        backgroundColor: primitive.$white,
      }}
      footer={
        <Button
          width="full"
          onPress={handleOnDismiss}
          accessibilityLabel={callToActionText}
          accessibilityRole="button"
        >
          {callToActionText}
        </Button>
      }
      scrollView
      headerText={pageTitle}
    >
      <View focusable accessibilityLabel={`${pageTitle} steps`}>
        {steps.map((step, index) => {
          const stepImage = step.stepImage?.locale;
          const stepImageAlt = stepImage?.asset?.altText || `Step ${index + 1} illustration`;
          const stepTitle = step.stepTitle?.locale || '';
          const stepDescription = blockContentToPlainText(step.stepDescription.localeRaw);

          return (
            <View key={step._key}>
              <View
                style={styles.stepImageContainer}
                accessibilityRole="image"
                accessibilityLabel={stepImageAlt}
                accessibilityHint={`Illustration for step ${index + 1}`}
                focusable
              >
                {stepImage && (
                  <Picture width="270px" image={stepImage} alt={stepImageAlt} objectFitContain />
                )}
              </View>
              <View
                style={[
                  styles.stepContentContainer,
                  {
                    ...(isDesktopWeb && {
                      paddingVertical: styles.stepContentContainer.paddingVertical * 2,
                      paddingHorizontal: 0,
                    }),
                  },
                ]}
                focusable
              >
                <View
                  style={styles.stepContentNumberContainer}
                  accessibilityRole="text"
                  accessibilityLabel={`Step ${index + 1} of ${steps.length}`}
                  accessible
                  focusable
                >
                  <Text style={styles.stepContentNumber}>{index + 1}</Text>
                </View>
                <View style={styles.stepContentTextContainer}>
                  <View
                    focusable
                    accessibilityRole="header"
                    accessibilityLevel={2}
                    accessible
                    accessibilityLabel={`Step ${index + 1} title: ${stepTitle}`}
                  >
                    <Text style={styles.stepContentTitle}>{stepTitle}</Text>
                  </View>
                  <View
                    focusable
                    accessibilityRole="text"
                    accessible
                    accessibilityLabel={`Step ${index + 1} description: ${stepDescription}`}
                  >
                    <Text style={styles.stepContentDescription}>{stepDescription}</Text>
                  </View>
                </View>
              </View>
            </View>
          );
        })}
      </View>
    </CardModal>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: primitive.$white,
  },
  stepImage: {
    width: 270,
    height: 270,
  },
  stepImageContainer: {
    alignItems: 'center',
    backgroundColor: primitive.bk.$bbqBrown,
  },
  stepContentContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 24,
    backgroundColor: primitive.$white,
  },
  stepContentNumberContainer: {
    marginRight: 12,
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 100,
    width: 25,
    height: 25,
  },
  stepContentNumber: {
    color: primitive.bk.$bbqBrown,
    borderColor: primitive.bk.$bbqBrown,
    fontFamily: primitive.fontFamily.$body,
    textAlign: 'center',
    fontSize: 12,

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
  },
  stepContentTitle: {
    fontSize: primitive.fontSizes.md,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
    marginBottom: 8,
  },
  stepContentDescription: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    fontSize: 14,
    marginBottom: 8,
  },
  stepContentTextContainer: {
    flex: 1,
  },
});
